// Ceiling-light PLANNING — the three styles the app can lay out on a ceiling,
// and the objects each one produces. Pure: no React, no Firestore.
//
// Extracted from `useCeilingLightEditor` so the Fixtures & Finishes panel's
// Ceiling Lighting row and the ceiling editor plan the SAME lights. They are
// two entry points onto one decision, and a second copy of the recessed-vs-puck
// center contract below is exactly the kind of drift that puts a can half
// through the ceiling on one surface and flush on the other.
const { layoutRecessedLights } = require('../../lighting/layoutRecessedLights');
const {
  calculatePolygonCentroid,
} = require('../../vanity-assembly/placementUtils');
const { deriveFlatCeilingY } = require('../../lighting/ceilingSeat');
const {
  INSPECTED_PRODUCER,
} = require('../../existing-conditions/inspectedDevices');
const {
  DRUM_LIGHT_MODEL_ID,
  isCeilingLight,
  isRecessedCan,
} = require('./lightingClassification');

/**
 * Light style configurations.
 * Grid spacing is owned by the shared layout engine (layoutRecessedLights).
 */
/** A recessed can's size — also used when one is placed by hand. */
const RECESSED_LIGHT_DIMENSIONS = { x: 0.1016, y: 0.0254, z: 0.1016 }; // 4" dia x 1" deep

const LIGHT_STYLES = {
  recessed: {
    label: 'Recessed',
    type: 'grid',
    dims: RECESSED_LIGHT_DIMENSIONS,
    proceduralStyle: 'recessed',
    useGlbModel: false,
  },
  puck: {
    label: 'Puck',
    type: 'grid',
    dims: { x: 0.1, y: 0.05, z: 0.1 },
    proceduralStyle: null,
    useGlbModel: false,
  },
  drum: {
    label: 'Drum',
    type: 'centered',
    dims: { x: 0.6, y: 0.15, z: 0.6 },
    proceduralStyle: null,
    useGlbModel: true,
  },
};

/**
 * Which of the three styles above a ceiling light IS, or null for a ceiling
 * light the editor does not manage.
 *
 * The editor owns only what it can lay out again: a recessed can, a plain puck
 * and the drum GLB. A pendant (`lightingSubtype`) and a catalog fixture (any
 * other `modelId` — a chandelier, a flush mount) share `type: 'ceiling-light'`
 * and the facet's `parentCeilingId`, but they belong to Fixtures & Finishes and
 * the properties panel, so counting, replacing and removing all skip them.
 * So does a light the inspection FOUND (`addedBy: 'existing-conditions'`),
 * even a recessed can: it is a fixture the house has, not a layout the editor
 * made, and laying the ceiling out again must not delete it.
 */
function ceilingLightStyleOf(obj) {
  if (!isCeilingLight(obj) || obj.lightingSubtype) return null;
  if (obj.addedBy === INSPECTED_PRODUCER) return null;
  if (isRecessedCan(obj)) return 'recessed';
  if (obj.modelId === DRUM_LIGHT_MODEL_ID) return 'drum';
  if (!obj.modelId && !obj.proceduralStyle) return 'puck';
  return null;
}

/** Is this one of the lights the editor manages on `ceilingId`? */
function isManagedCeilingLightOn(obj, ceilingId) {
  return obj?.parentCeilingId === ceilingId && ceilingLightStyleOf(obj) != null;
}

/**
 * Count the editor-managed lights on a given ceiling in a room.
 */
function countCeilingLights(roomData, ceilingId) {
  return (roomData?.objects || []).filter((obj) =>
    isManagedCeilingLightOn(obj, ceilingId)
  ).length;
}

/**
 * Detect which style the editor-managed lights on a ceiling match. A facet the
 * editor laid out holds one style; a hand-placed light beside what a tool laid
 * (one with no `addedBy`, which a re-lay keeps) does not change it, so the
 * laid lights name the style when there are any. A hand-mixed facet reports
 * the first present in recessed → drum → puck order.
 */
function detectCurrentStyle(roomData, ceilingId) {
  const managed = (roomData?.objects || []).filter((obj) =>
    isManagedCeilingLightOn(obj, ceilingId)
  );
  const laid = managed.filter((obj) => obj.addedBy);
  const styles = new Set(
    (laid.length ? laid : managed).map(ceilingLightStyleOf)
  );
  return ['recessed', 'drum', 'puck'].find((s) => styles.has(s)) ?? null;
}

/**
 * Build light objects for a ceiling given a style key, ceiling data, and room data.
 */
function buildLights(styleKey, ceilingData, roomData) {
  const config = LIGHT_STYLES[styleKey];
  if (!config) return [];
  const polygonCorners = ceilingData?.polygonCorners;
  if (!polygonCorners || polygonCorners.length < 3) return [];

  const ceilingId = ceilingData?.id || null;
  const now = Date.now();

  if (config.type === 'grid') {
    const isRecessedStyle = config.proceduralStyle === 'recessed';

    // Layout over this facet's footprint, seated against ALL room facets so
    // a soffit or slope elsewhere can't misplace a can; keep only cans that
    // seated on THIS facet (per-facet apply model).
    const layouts = layoutRecessedLights({
      floorPolygon: polygonCorners.map((p) => ({ x: p.x, z: p.z })),
      ceilings: roomData?.ceilings?.length ? roomData.ceilings : [ceilingData],
      walls: roomData?.walls || [],
      objects: roomData?.objects || [],
    }).filter((L) => L.ceilingId == null || L.ceilingId === ceilingId);

    return layouts.map((L, index) => {
      const light = {
        id: `ceiling-light_${now}_${ceilingId || 'c'}_${index}`,
        type: 'ceiling-light',
        label:
          layouts.length > 1 ? `Ceiling Light ${index + 1}` : 'Ceiling Light',
        category: { 'ceiling-light': true },
        // Renderer contract differs by style: the plain-puck renderer is
        // dimensions-blind and seats the puck body ABOVE center.y with its
        // bottom face flush at center.y (center = surface), while the
        // recessed-can renderer treats center as the body midpoint, half
        // the can depth below the ceiling surface along the facet normal
        // (so the trim ring lands flush on flat AND sloped facets).
        center: isRecessedStyle
          ? {
              x: L.surface.x - L.normal.x * (config.dims.y / 2),
              y: L.surface.y - L.normal.y * (config.dims.y / 2),
              z: L.surface.z - L.normal.z * (config.dims.y / 2),
            }
          : { x: L.surface.x, y: L.surface.y, z: L.surface.z },
        dimensions: { ...config.dims },
        rotation: L.rotation,
        parentCeilingId: L.ceilingId ?? ceilingId,
        addedBy: 'ceiling_light_editor',
        lastModified: new Date().toISOString(),
      };
      if (config.proceduralStyle)
        light.proceduralStyle = config.proceduralStyle;
      return light;
    });
  }

  // centered (drum): unchanged placement, shared elevation derivation
  const centroid = calculatePolygonCentroid(polygonCorners);
  if (!centroid) return [];
  // An ELEVATION, not a height. null means no ceiling and no measurable wall
  // top, so there is nothing to hang from — emit nothing rather than a light
  // at world zero (see deriveFlatCeilingY).
  const ceilingY = deriveFlatCeilingY(
    ceilingData ? [ceilingData] : [],
    roomData?.walls || []
  );
  if (ceilingY == null) return [];
  const light = {
    id: `ceiling-light_${now}_${ceilingId || 'c'}_0`,
    type: 'ceiling-light',
    label: 'Ceiling Light',
    category: { 'ceiling-light': true },
    center: {
      x: centroid.x,
      y: ceilingY - config.dims.y / 2,
      z: centroid.z,
    },
    dimensions: { ...config.dims },
    rotation: { x: 0, y: 0, z: 0 },
    parentCeilingId: ceilingId,
    addedBy: 'ceiling_light_editor',
    lastModified: new Date().toISOString(),
  };
  if (config.useGlbModel) light.modelId = DRUM_LIGHT_MODEL_ID;
  return [light];
}

module.exports = {
  CEILING_LIGHT_STYLES: LIGHT_STYLES,
  DRUM_LIGHT_MODEL_ID,
  buildCeilingLights: buildLights,
  ceilingLightStyleOf,
  isManagedCeilingLightOn,
  countCeilingLights,
  detectCeilingLightStyle: detectCurrentStyle,
  RECESSED_LIGHT_DIMENSIONS,
};
